'use client'
import { Button } from '@payloadcms/ui'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { useLocale } from '@/components/LocaleContext'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import {
  RequestForStockModal,
  type StockRequestLine,
} from '@/components/stock/RequestForStockModal'
import { CART_UPDATED_EVENT, emitCartUpdated } from '@/utilities/eventEmitter'

const min = 1,
  max = 9999

function clampQuantity(value: number): number {
  return Math.max(min, Math.min(max, value))
}

function parseQuantityInput(value: string): number {
  const parsed = parseInt(value.replace(/\D/g, ''), 10)
  if (!Number.isFinite(parsed)) return min
  return clampQuantity(parsed)
}

export const Interact = ({
  id,
  code,
  partNumber,
  rate,
  isDealer,
  stockQty = 0,
  productName,
}: {
  id: string
  code: string
  partNumber?: string
  rate: string | null
  isDealer: boolean
  stockQty?: number
  productName?: string
}) => {
  const { locale } = useLocale()
  const dialogDir = locale === 'ar' ? 'rtl' : 'ltr'

  const tByLocale: Record<
    string,
    {
      failedAddToCart: string
      cannotAddToCart: string
      addingToCart: string
      addedToCart: string
      datasheetDownloadFailed: string
      datasheetUnauthorized: string
      addToCartLabel: string
      downloadDatasheetAriaLabel: string
      submitEnquiry: string
      insufficientStockTitle: string
      insufficientStockDescription: (available: number) => string
      proceedAvailableTitle: string
      proceedAvailableMessage: string
      alreadyMaxInCart: (available: number) => string
      no: string
      yes: string
    }
  > = {
    en: {
      failedAddToCart: 'Failed to add to cart',
      cannotAddToCart: 'This product cannot be added to cart',
      addingToCart: 'Adding to cart...',
      addedToCart: 'Added to cart!',
      datasheetDownloadFailed: 'Could not download datasheet (PDF).',
      datasheetUnauthorized: 'Sign in to download the datasheet.',
      addToCartLabel: 'Add to cart',
      downloadDatasheetAriaLabel: 'Download product datasheet (PDF)',
      submitEnquiry: 'Submit Enquiry',
      insufficientStockTitle: 'Insufficient stock',
      insufficientStockDescription: (available) =>
        `We only have ${available} qty in stock. Would you like to raise a request for the remaining qty?`,
      proceedAvailableTitle: 'Proceed with available qty?',
      proceedAvailableMessage: 'Are you sure you want to proceed with only the available qty?',
      alreadyMaxInCart: (available) =>
        `Only ${available} available. Your cart already has the maximum quantity.`,
      no: 'No',
      yes: 'Yes',
    },
    fr: {
      failedAddToCart: "Échec de l'ajout au panier",
      cannotAddToCart: 'Ce produit ne peut pas être ajouté au panier',
      addingToCart: 'Ajout au panier...',
      addedToCart: 'Ajouté au panier !',
      datasheetDownloadFailed: 'Impossible de télécharger la fiche technique (PDF).',
      datasheetUnauthorized: 'Connectez-vous pour télécharger la fiche technique.',
      addToCartLabel: 'Ajouter au panier',
      downloadDatasheetAriaLabel: 'Telecharger la fiche technique du produit (PDF)',
      submitEnquiry: 'Envoyer la demande',
      insufficientStockTitle: 'Stock insuffisant',
      insufficientStockDescription: (available) =>
        `Nous n’avons que ${available} unités en stock. Souhaitez-vous faire une demande pour la quantité restante ?`,
      proceedAvailableTitle: 'Continuer avec le stock disponible ?',
      proceedAvailableMessage:
        'Voulez-vous vraiment continuer avec uniquement la quantité disponible ?',
      alreadyMaxInCart: (available) =>
        `Seulement ${available} disponibles. Votre panier contient déjà la quantité maximale.`,
      no: 'Non',
      yes: 'Oui',
    },
    ar: {
      failedAddToCart: 'فشل إضافة المنتج إلى السلة',
      cannotAddToCart: 'لا يمكن إضافة هذا المنتج إلى السلة',
      addingToCart: 'جارٍ الإضافة إلى السلة...',
      addedToCart: 'تمت الإضافة إلى السلة!',
      datasheetDownloadFailed: 'تعذّر تنزيل ورقة البيانات (PDF).',
      datasheetUnauthorized: 'سجّل الدخول لتنزيل ورقة البيانات.',
      addToCartLabel: 'إضافة إلى السلة',
      downloadDatasheetAriaLabel: 'تنزيل ورقة بيانات المنتج (PDF)',
      submitEnquiry: 'إرسال الطلب',
      insufficientStockTitle: 'مخزون غير كافٍ',
      insufficientStockDescription: (available) =>
        `لدينا فقط ${available} قطعة في المخزون. هل تريد تقديم طلب للكمية المتبقية؟`,
      proceedAvailableTitle: 'المتابعة بالكمية المتوفرة؟',
      proceedAvailableMessage: 'هل أنت متأكد أنك تريد المتابعة بالكمية المتوفرة فقط؟',
      alreadyMaxInCart: (available) =>
        `متوفر فقط ${available} قطعة. سلتك تحتوي بالفعل على الحد الأقصى من الكمية.`,
      no: 'لا',
      yes: 'نعم',
    },
  }
  const t = tByLocale[locale] ?? tByLocale.en
  const [quantityInput, setQuantityInput] = useState('1')
  const [loading, setLoading] = useState(false)
  const [datasheetLoading, setDatasheetLoading] = useState(false)
  const [stockModalOpen, setStockModalOpen] = useState(false)
  const [showInsufficientStockConfirm, setShowInsufficientStockConfirm] = useState(false)
  const [showPartialConfirm, setShowPartialConfirm] = useState(false)
  const [pendingRequestedQty, setPendingRequestedQty] = useState<number | null>(null)
  const cartItemQtyRef = useRef(0)
  const skipNextCartRefetch = useRef(false)

  const fetchCartItemQty = useCallback(async (): Promise<number> => {
    if (!id) return cartItemQtyRef.current

    try {
      const params = new URLSearchParams({ productId: id })
      if (code) params.set('code', code)

      const res = await fetch(`/api/cart/item-qty?${params.toString()}`, {
        credentials: 'include',
      })
      if (!res.ok) return cartItemQtyRef.current

      const data = (await res.json()) as { qty?: number }
      const qty = Number.isInteger(data.qty) ? data.qty! : 0
      cartItemQtyRef.current = qty
      return qty
    } catch {
      return cartItemQtyRef.current
    }
  }, [id, code])

  useEffect(() => {
    if (isDealer && stockQty > 0) {
      void fetchCartItemQty()
    }
  }, [isDealer, stockQty, fetchCartItemQty])

  useEffect(() => {
    const handleCartUpdated = () => {
      if (skipNextCartRefetch.current) {
        skipNextCartRefetch.current = false
        return
      }

      if (isDealer && stockQty > 0) {
        void fetchCartItemQty()
      }
    }

    window.addEventListener(CART_UPDATED_EVENT, handleCartUpdated)
    return () => window.removeEventListener(CART_UPDATED_EVENT, handleCartUpdated)
  }, [isDealer, stockQty, fetchCartItemQty])

  const isOutOfStock = isDealer && stockQty <= 0
  const hasPrice = Boolean(rate)
  const canAddToCart = isDealer ? hasPrice && !isOutOfStock : true

  const buildStockModalLines = (requestedQty: number): StockRequestLine[] => {
    const shortfall = stockQty > 0 ? Math.max(0, requestedQty - stockQty) : requestedQty
    return [
      {
        productCode: code,
        partNumber: partNumber?.trim() || undefined,
        productName,
        requestedQty,
        availableQty: stockQty,
        shortfallQty: shortfall || requestedQty,
      },
    ]
  }

  const stockModalLines =
    pendingRequestedQty != null
      ? buildStockModalLines(pendingRequestedQty)
      : buildStockModalLines(1)

  const openExceedStockFlow = (requestedTotalQty: number) => {
    setPendingRequestedQty(requestedTotalQty)
    setShowInsufficientStockConfirm(true)
  }

  const resetStockFlow = () => {
    setPendingRequestedQty(null)
    setShowInsufficientStockConfirm(false)
    setShowPartialConfirm(false)
  }

  const safePdfBasename = (raw: string) => {
    const cleaned = raw
      .replace(/[^\w\-+.]/g, '_')
      .replace(/_+/g, '_')
      .trim()
    return cleaned.length > 0 ? cleaned.slice(0, 120) : 'product'
  }

  const getFilenameFromContentDisposition = (contentDisposition: string | null): string | null => {
    if (!contentDisposition) return null
    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)
    if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1].replace(/^["']|["']$/g, ''))
    const plainMatch = contentDisposition.match(/filename="?([^"]+)"?/i)
    if (plainMatch?.[1]) return plainMatch[1]
    return null
  }

  const handleDownloadDatasheet = async () => {
    setDatasheetLoading(true)
    try {
      const res = await fetch(`/api/products/${id}/download`, {
        credentials: 'same-origin',
      })
      if (res.status === 401) {
        toast.error(t.datasheetUnauthorized)
        return
      }
      if (!res.ok) {
        toast.error(t.datasheetDownloadFailed)
        return
      }
      const blob = await res.blob()
      const url = URL.createObjectURL(
        blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' }),
      )
      const a = document.createElement('a')
      a.href = url
      const filenameFromHeader = getFilenameFromContentDisposition(
        res.headers.get('Content-Disposition'),
      )
      a.download = filenameFromHeader ?? `${safePdfBasename(`Gallium_Datasheet_${code}`)}.pdf`
      a.rel = 'noopener'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch {
      toast.error(t.datasheetDownloadFailed)
    } finally {
      setDatasheetLoading(false)
    }
  }

  const addToCartWithQty = async (qty: number) => {
    if (!id || !code) {
      toast.error(t.failedAddToCart)
      return
    }
    if (!canAddToCart) {
      toast.error(t.cannotAddToCart)
      return
    }

    setLoading(true)

    try {
      const response = await fetch('/api/cart/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          productId: id,
          qty,
          code,
          rate,
          enforceStockLimit: true,
        }),
      })

      if (response.status === 409) {
        const data = (await response.json().catch(() => ({}))) as {
          code?: string
          nextCartQty?: number
          currentCartQty?: number
        }

        if (data.code === 'INSUFFICIENT_STOCK') {
          if (Number.isInteger(data.currentCartQty)) {
            cartItemQtyRef.current = data.currentCartQty!
          }
          const requestedTotal = Number.isInteger(data.nextCartQty)
            ? data.nextCartQty!
            : cartItemQtyRef.current + qty
          openExceedStockFlow(requestedTotal)
          return
        }
      }

      if (!response.ok) {
        toast.error(t.failedAddToCart)
        return
      }

      skipNextCartRefetch.current = true
      cartItemQtyRef.current += qty
      emitCartUpdated()
      toast.success(t.addedToCart)
    } catch (err) {
      console.error(err)
      toast.error(t.failedAddToCart)
    } finally {
      setLoading(false)
    }
  }

  const handleAddToCart = () => {
    const quantity = parseQuantityInput(quantityInput)
    setQuantityInput(String(quantity))

    const currentCartQty = cartItemQtyRef.current
    const requestedTotal = currentCartQty + quantity

    if (isDealer && stockQty > 0 && (currentCartQty >= stockQty || requestedTotal > stockQty)) {
      openExceedStockFlow(requestedTotal)
      return
    }

    void addToCartWithQty(quantity)
  }

  const handleQuantityChange = (value: string) => {
    setQuantityInput(value.replace(/\D/g, '').slice(0, 4))
  }

  const handleQuantityBlur = () => {
    setQuantityInput(String(parseQuantityInput(quantityInput)))
  }

  const decrement = () => {
    const next = clampQuantity(parseQuantityInput(quantityInput) - 1)
    setQuantityInput(String(next))
  }

  const increment = () => {
    const next = clampQuantity(parseQuantityInput(quantityInput) + 1)
    setQuantityInput(String(next))
  }

  const currentQuantity = parseQuantityInput(quantityInput)

  const handleOpenOutOfStockModal = () => {
    setPendingRequestedQty(null)
    setStockModalOpen(true)
  }

  const handleRequestProduct = () => {
    setPendingRequestedQty(currentQuantity)
    setStockModalOpen(true)
  }

  return (
    <>
      <div className="mt-6 flex flex-wrap items-center gap-3">
        {isOutOfStock ? (
          <Button
            buttonStyle="none"
            onClick={handleOpenOutOfStockModal}
            className="inline-flex h-10 items-center justify-center bg-[var(--gallium-red)] px-6 font-poppins text-sm font-semibold uppercase text-white hover:bg-red-900"
          >
            {t.submitEnquiry}
          </Button>
        ) : (
          <>
            <div className="flex items-center border border-neutral-300 rounded-md overflow-hidden">
              <button
                type="button"
                onClick={decrement}
                disabled={currentQuantity <= min}
                className="flex h-10 w-10 items-center justify-center bg-[var(--gallium-red)] text-white disabled:opacity-50"
              >
                −
              </button>

              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="off"
                value={quantityInput}
                onChange={(e) => handleQuantityChange(e.target.value)}
                onBlur={handleQuantityBlur}
                className="flex h-10 w-14 min-w-[3rem] border-0 bg-white text-center font-medium text-[#111] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gallium-red)] focus-visible:ring-inset [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                aria-label="Quantity"
              />

              <button
                type="button"
                onClick={increment}
                disabled={currentQuantity >= max}
                className="flex h-10 w-10 items-center justify-center bg-[var(--gallium-red)] text-white disabled:opacity-50"
              >
                +
              </button>
            </div>
            <Button
              buttonStyle="none"
              disabled={loading}
              onClick={
                isDealer ? (!hasPrice ? handleRequestProduct : handleAddToCart) : handleAddToCart
              }
              className="inline-flex h-10 items-center justify-center bg-[var(--gallium-red)] px-6 font-poppins text-sm font-semibold uppercase text-white disabled:opacity-50 hover:bg-red-900"
            >
              {isDealer ? (!hasPrice ? t.submitEnquiry : t.addToCartLabel) : t.addToCartLabel}
            </Button>
          </>
        )}
        {isDealer && (
          <button
            type="button"
            onClick={handleDownloadDatasheet}
            disabled={datasheetLoading}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-neutral-300 bg-white text-[#111] hover:bg-neutral-50 focus:outline-none disabled:opacity-50"
            aria-label={t.downloadDatasheetAriaLabel}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </button>
        )}
      </div>

      <ConfirmDialog
        isOpen={showInsufficientStockConfirm}
        title={t.insufficientStockTitle}
        message={t.insufficientStockDescription(stockQty)}
        yesLabel={t.yes}
        noLabel={t.no}
        dir={dialogDir}
        onConfirm={() => {
          setShowInsufficientStockConfirm(false)
          setStockModalOpen(true)
        }}
        onCancel={() => {
          setShowInsufficientStockConfirm(false)
          setShowPartialConfirm(true)
        }}
      />

      <ConfirmDialog
        isOpen={showPartialConfirm}
        title={t.proceedAvailableTitle}
        message={t.proceedAvailableMessage}
        yesLabel={t.yes}
        noLabel={t.no}
        dir={dialogDir}
        onConfirm={() => {
          const remainingQty = Math.max(0, stockQty - cartItemQtyRef.current)
          resetStockFlow()

          if (remainingQty > 0) {
            void addToCartWithQty(remainingQty)
            return
          }

          toast.info(t.alreadyMaxInCart(stockQty))
        }}
        onCancel={() => {
          resetStockFlow()
        }}
      />

      <RequestForStockModal
        isOpen={stockModalOpen}
        onClose={() => {
          setStockModalOpen(false)
          setPendingRequestedQty(null)
        }}
        lines={stockModalLines}
        locale={(locale as AppLocale) ?? 'en'}
        submitMode="standalone"
        onSuccess={() => {
          setPendingRequestedQty(null)
        }}
      />
    </>
  )
}

export default Interact
